import { useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router';

import URLSearchParamsEx from '#utils/url-ex';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import { useGetProductMutation } from '#services/product';
import { useGetCollectionsForUserMutation } from '#services/collection';
import { useGetActiveVendorsQuery } from '#services/vendor';

export type ProductFormState = Record<string, unknown>;

export interface ProductVariant {
  id: string;
  variant: string;
  price: number | string;
  index: number;
  inventoryOnly: boolean;
  [key: string]: unknown;
}

const EMPTY_ACTIVE_VENDORS: unknown[] = [];

export const composeVariants = (
  sizes: string[],
  colors: string[],
  lastVariants: ProductVariant[],
  defaultPrice: number,
): ProductVariant[] => {
  const comb: ProductVariant[] = [];
  let i = 0;
  colors.forEach((c) => {
    sizes.forEach((s) => {
      // get last value
      const id = `${s} / ${c}`;
      const find = lastVariants.find((lv) => lv.id === id);
      const variantPrice = find ? find.price : defaultPrice;
      const inventoryOnly = find ? find.inventoryOnly : false;

      comb.push({
        id,
        variant: id,
        price: variantPrice,
        index: i,
        inventoryOnly,
      });
      i += 1;
    });
  });

  return comb;
};

export const getInitialState = (
  plainData: unknown,
  { shippingWindowInWeeks, partial }: { shippingWindowInWeeks: number | string; partial?: boolean },
): ProductFormState => {
  const initialState = {
    inactivated: true,
    name: '',
    description: '',
    price: 0,
    sizes: [] as string[],
    colors: [] as string[],
    variants: [] as ProductVariant[],
    customizations: [] as Array<Record<string, unknown>>,
    shippingWindow: [{
      name: 'Regular',
      weeks: shippingWindowInWeeks,
      price: 0,
    }, {
      name: '',
      weeks: '',
      price: '',
    }] as Array<Record<string, unknown>>,
    vendor: '',

    availableCollections: [] as unknown[],
    availableVendors: [] as unknown[],
    collectionIds: [] as unknown[],
    newCollections: [] as unknown[],

    tags: [] as unknown[],
    media: [] as unknown[],
    showVideoFirst: false,
    videos: [] as Array<Record<string, unknown>>,
    total: 0,
    index: 0,

    inventoryProduct: [] as unknown[],
    featured: 0,
  };

  // workaround for redux toolkit
  // TODO use redux to handle the state
  const state: Record<string, unknown> = plainData
    ? JSON.parse(JSON.stringify(plainData))
    : { ...initialState };

  if (partial) {
    return state;
  }

  const variants = state.variants as ProductVariant[] | undefined;
  if (!variants || !variants.length) {
    state.variants = composeVariants(
      (state.sizes as string[]) || [],
      (state.colors as string[]) || [],
      [],
      initialState.price,
    );
  }

  const customizations = (state.customizations as Array<Record<string, unknown>>) || [];
  state.customizations = customizations;

  const lastCustomization = customizations[customizations.length - 1];
  if ((customizations.length === 0)
    || (lastCustomization && (lastCustomization.price || lastCustomization.name))) {
    customizations.push({ name: '', price: '' });
  }

  const videos = (state.videos as Array<Record<string, unknown>>) || [];
  state.videos = videos;
  const lastVideo = videos[videos.length - 1];
  if ((videos.length === 0)
    || (lastVideo && (lastVideo.url))) {
    videos.push({
      url: '',
    });
  }

  return state;
};

export function duplicateItem(values: ProductFormState): ProductFormState {
  return {
    ...values,
    id: null,
    media: [],
    files: [],
    inactivated: true,
    name: `copy of ${values.name as string}`,

    // reset for the inventory product
    inventoryProduct: [],
    variants: (values.variants as ProductVariant[]).map((v) => ({
      ...v,
      inventoryOnly: false,
    })),
  };
}

export function useData({
  resetForm,
  isDuplicateMode,
  isEditMode,
  shippingWindowInWeeks,
}: {
  resetForm: (state: ProductFormState) => void;
  isDuplicateMode: boolean;
  isEditMode: boolean;
  shippingWindowInWeeks: number;
}) {
  const location = useLocation();
  const createModeInitializedKeyRef = useRef<string | null>(null);
  const {
    data: activeVendors = EMPTY_ACTIVE_VENDORS,
    isSuccess: hasLoadedActiveVendors,
    isError: vendorQueryFailed,
  } = useGetActiveVendorsQuery(undefined, {
    skip: isEditMode || isDuplicateMode,
  });

  const {
    showBackdrop,
    hideBackdrop,
    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [getProduct] = useGetProductMutation();
  const [getCollectionsForUser] = useGetCollectionsForUserMutation();

  useEffect(() => {
    if (isEditMode || isDuplicateMode || (!hasLoadedActiveVendors && !vendorQueryFailed)) {
      return;
    }

    // Guard: only initialize once per navigation (location.key).
    // hasLoadedActiveVendors can flip false→true again on cache refetch/
    // invalidation (e.g. after a vendor CRUD), which would re-run this effect
    // and call resetForm while the user is filling the form.
    if (createModeInitializedKeyRef.current === location.key) {
      return;
    }
    createModeInitializedKeyRef.current = location.key;

    // create new product — show backdrop so the form is not interactable
    // until collections are loaded, preventing a race where resetForm fires
    // after the user has already started filling fields and wipes isDirty.
    showBackdrop();
    void getCollectionsForUser()
      .then((res) => {
        simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);

        resetForm(getInitialState({
          availableCollections: (res as { data?: unknown }).data,
          availableVendors: activeVendors,
        }, {
          shippingWindowInWeeks,
          partial: true,
        }));
      })
      .finally(() => {
        hideBackdrop();
      });
  }, [location.key, hasLoadedActiveVendors, vendorQueryFailed]);

  useEffect(() => {
    if (!isEditMode && !isDuplicateMode) {
      return;
    }

    const urlParam = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    const productBody = isDuplicateMode
      ? { productId: urlParam.values.duplicate }
      : urlParam.values;

    showBackdrop();
    void getProduct(productBody as Parameters<typeof getProduct>[0])
      .then((res) => {
        const product = (res as { data?: unknown }).data;
        if (!product) {
          simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
          return;
        }

        resetForm(getInitialState(
          isDuplicateMode ? duplicateItem(product as ProductFormState) : product,
          { shippingWindowInWeeks },
        ));
      })
      .finally(() => {
        hideBackdrop();
      });
  }, [location.key]);
}
